import { lazy, useEffect, useRef } from 'react'
import { Routes, Route, useLocation } from 'react-router-dom'
import { AuthProvider, RequireAuth } from './auth'
import { RequireAdmin } from '../shared/components/RequireAdmin'
import DashboardLayout from '../shared/layouts/DashboardLayout'
import { trackPageView } from '../shared/api'

// Login + Signup are eagerly loaded (public entry points)
import Login from '../features/dashboard/Login'
import Signup from '../features/dashboard/Signup'

/**
 * Fires a pageview event on every route change.
 *
 * Dedupe guard: if the same path fires within 500ms (React StrictMode
 * double-invoke in dev), only the first call goes out.
 */
function PageViewTracker() {
  const location = useLocation()
  const lastRef = useRef<{ path: string; ts: number } | null>(null)

  useEffect(() => {
    const now = Date.now()
    const last = lastRef.current
    const isSamePathWithin500ms =
      last !== null && last.path === location.pathname && now - last.ts < 500

    if (!isSamePathWithin500ms) {
      lastRef.current = { path: location.pathname, ts: now }
      trackPageView(location.pathname, document.referrer || null)
    }
  }, [location.pathname])

  return null
}

// Atlas LNG is a single product — FSRU is a separate repo, ignored here.
// Lazy-loaded feature pages for code splitting.
const Dashboard = lazy(() => import('../features/dashboard/Dashboard'))
const LiveView = lazy(() => import('../features/vessels/LiveView'))
const VoyagePerformance = lazy(() => import('../features/voyage-performance/VoyagePerformance'))
const VesselReports = lazy(() => import('../features/vessel-reports/VesselReports'))
const Graphs = lazy(() => import('../features/graphs/Graphs'))
const Reports = lazy(() => import('../features/reports/Reports'))
const Scheduler = lazy(() => import('../features/scheduler/Scheduler'))
const Notifications = lazy(() => import('../features/messages/Messages'))
const Admin = lazy(() => import('../features/admin/Admin'))
const CalculationExplainer = lazy(() => import('../features/calculation-explainer/CalculationExplainer'))

function App() {
  return (
    <AuthProvider>
      <PageViewTracker />
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<Login />} />
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />

        <Route
          path="/lng-ops"
          element={
            <RequireAuth>
              <DashboardLayout />
            </RequireAuth>
          }
        >
          <Route index element={<Dashboard />} />
          <Route path="live-view" element={<LiveView />} />
          <Route path="voyage-performance" element={<VoyagePerformance />} />
          <Route path="vessel-reports" element={<VesselReports />} />
          <Route path="graphs" element={<Graphs />} />
          <Route path="reports" element={<Reports />} />
          <Route path="scheduler" element={<Scheduler />} />
          <Route path="notifications" element={<Notifications />} />
          <Route path="calculations" element={<CalculationExplainer />} />
          <Route path="admin" element={<RequireAdmin><Admin /></RequireAdmin>} />
        </Route>

        {/* 404 → login */}
        <Route path="*" element={<Login />} />
      </Routes>
    </AuthProvider>
  )
}

export default App
